import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {BrandCheckboxCheckedIcon, BrandGuideIcon, DownloadIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Modal} from "@pg-design/modal-module";
import {CheckboxControlled, InputField} from "@pg-design/rhf-components-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {rpAppLink} from "@pg-mono/rp-routes";
import {userComAttachmentDownload} from "@pg-mono/user-com";

import {IRPStore} from "../../app/rp_reducer";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {RodoText} from "../../lead/components/RodoText";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useArticleAttachmentsLSStore} from "../hooks/use_article_attachment_ls_store";

interface IEbookCardProps {
    title: string;
    source: string;
    downloadURL: string;
}

const schema = Yup.object().shape({
    email: Yup.string().email().required(),
    optIn: Yup.boolean().required().oneOf([true], "Zgoda jest wymagana")
});

export const ArticleEbookCard = (props: IEbookCardProps) => {
    const {source, title, downloadURL} = props;
    const [modalPage, setModalPage] = useState<"form" | "success" | null>(null);
    const target = useResponsiveLinkTarget();
    const isAuthenticated = useIsAuthenticated();
    const {email: articleAttachmentSavedEmail, storeEmail} = useArticleAttachmentsLSStore();

    // get available user data
    const {data: profileData} = useGetUserProfileQuery();
    const optInFromProfile = profileData?.email_marketing_consent;
    const emailFromProfile = profileData?.email;

    const sentLeadFormValues = useSelector((state: IRPStore) => state.sentLead.storedValues);
    const emailFromStorage = sentLeadFormValues?.email;
    const userEmail = emailFromStorage || emailFromProfile;

    // form setup
    const defaultFormValuesFromProfile = {
        email: userEmail,
        optIn: optInFromProfile
    };

    const methods = useFormRHF<{email: string; optIn: boolean}>({
        defaultValues: defaultFormValuesFromProfile,
        resolver: yupResolver(schema)
    });
    const {handleSubmit} = methods;

    useEffect(() => {
        if (defaultFormValuesFromProfile.email && methods.getValues("email") !== defaultFormValuesFromProfile.email) {
            methods.setValue("email", defaultFormValuesFromProfile.email);
        }
    }, [defaultFormValuesFromProfile]);

    const handleClick = () => {
        if ((userEmail && optInFromProfile) || isAuthenticated || articleAttachmentSavedEmail) {
            userComAttachmentDownload({
                email: methods.getValues("email"),
                attachmentTitle: props.title
            });

            window.open(props.downloadURL, target);
            return;
        }
        setModalPage("form");
    };

    const onSubmit = () => {
        storeEmail(methods.getValues("email"));
        userComAttachmentDownload({
            email: methods.getValues("email"),
            attachmentTitle: props.title,
            emailOptIn: methods.getValues("optIn")
        });

        setModalPage("success");
    };

    const onSecondStepDownload = () => {
        setModalPage(null);
    };

    return (
        <>
            <a className={ebookImageStyle} target={target} onClick={handleClick}>
                <Image className={pdfImg} src={source} alt={title} height="719px" width="465px" ratio={{xs: "465/719"}} />
            </a>
            {Boolean(modalPage) && (
                <Modal isOpen={Boolean(modalPage)} onModalClose={() => setModalPage(null)} modalTheme="light">
                    {modalPage === "form" ? (
                        <>
                            <div className={modalHeadingStyle}>
                                <BrandGuideIcon size="9" wrapperSize="10" wrapperType="circle" wrapperColor={getThemeVariable("colors.primary")} />
                                <div className={css({p: 2})}>
                                    <Text variant="headline_3">Pobierz darmowy materiał</Text>
                                    <Text variant="body_copy_2">Poszerz swoją wiedzę o zakupie mieszkania</Text>
                                </div>
                                <Modal.CloseButton placement="right" />
                            </div>
                            <Modal.Content className={modalStyle}>
                                <FormProviderRHF {...methods}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <InputField name="email" placeholder="e-mail" className={css({pb: 2})} disabled={Boolean(emailFromProfile)} />
                                        <CheckboxControlled
                                            id="optInCheckbox"
                                            name="optIn"
                                            labelContent="Chcę otrzymywać mailem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać."
                                            checkedMarkColor={getThemeVariable("globals.checkbox.checkedColor")}
                                            className={css({mb: 8})}
                                        />
                                        <Text variant="info_txt_2" align="center" color={getThemeVariable("colors.gray.700")} className={termsStyle}>
                                            Akceptuję{" "}
                                            <a href={rpAppLink.termsOfService.base()} target="_blank">
                                                regulamin serwisu
                                            </a>
                                        </Text>
                                        <Button variant="filled_primary" className={css({mb: 2})} fullWidth>
                                            pobierz
                                        </Button>
                                        <RodoText
                                            privacyPolicyUrl={rpAppLink.rodoTerms.base()}
                                            vendorName="Property Group sp. z o.o."
                                            privacyDataProcessorName={null}
                                            align="center"
                                        />
                                    </form>
                                </FormProviderRHF>
                            </Modal.Content>
                        </>
                    ) : (
                        <>
                            <BrandCheckboxCheckedIcon
                                className={successIconStyle}
                                size="9"
                                wrapperSize="10"
                                wrapperType="circle"
                                wrapperColor={getThemeVariable("colors.primary")}
                            />
                            <Modal.Content className={modalStyle}>
                                <div className={successTextWrapper}>
                                    <Text variant="headline_3" align="center">
                                        Materiał jest gotowy do pobrania
                                    </Text>
                                </div>
                                <Button
                                    href={downloadURL}
                                    target="_blank"
                                    variant="filled_primary"
                                    className={css({mb: 2})}
                                    iconLeft={DownloadIcon}
                                    onClick={onSecondStepDownload}
                                >
                                    pobierz
                                </Button>
                            </Modal.Content>
                        </>
                    )}
                </Modal>
            )}
        </>
    );
};

const ebookImageStyle = css({
    display: "block",
    width: "100%",
    cursor: "pointer"
});

const pdfImg = css({
    maxWidth: "32rem",
    width: "100%",
    height: "100%",
    marginLeft: "auto",
    marginRight: "auto",

    md: {
        maxWidth: "46.5rem"
    }
});

const modalStyle = css({
    p: 2,
    md: {
        p: 3,
        width: "556px"
    }
});

const modalHeadingStyle = css({
    p: 3,
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
});

const termsStyle = css({
    mb: 2,

    "& a, & a:hover, & a:visited, & a:active": {
        color: "inherit",
        textDecoration: "underline"
    }
});

const successTextWrapper = css({
    p: 2,
    mb: 2
});

const successIconStyle = css({
    mt: 2,
    mb: 0,
    mr: "auto",
    ml: "auto"
});
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {BrandCheckboxCheckedIcon, BrandGuideIcon, DownloadIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Modal} from "@pg-design/modal-module";
import {CheckboxControlled, InputField} from "@pg-design/rhf-components-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {rpAppLink} from "@pg-mono/rp-routes";
import {userComAttachmentDownload} from "@pg-mono/user-com";

import {IRPStore} from "../../app/rp_reducer";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {RodoText} from "../../lead/components/RodoText";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useArticleAttachmentsLSStore} from "../hooks/use_article_attachment_ls_store";

interface IEbookCardProps {
    title: string;
    source: string;
    downloadURL: string;
}

const schema = Yup.object().shape({
    email: Yup.string().email().required(),
    optIn: Yup.boolean().required().oneOf([true], "Zgoda jest wymagana")
});

export const ArticleEbookCard = (props: IEbookCardProps) => {
    const {source, title, downloadURL} = props;
    const [modalPage, setModalPage] = useState<"form" | "success" | null>(null);
    const target = useResponsiveLinkTarget();
    const isAuthenticated = useIsAuthenticated();
    const {email: articleAttachmentSavedEmail, storeEmail} = useArticleAttachmentsLSStore();

    // get available user data
    const {data: profileData} = useGetUserProfileQuery();
    const optInFromProfile = profileData?.email_marketing_consent;
    const emailFromProfile = profileData?.email;

    const sentLeadFormValues = useSelector((state: IRPStore) => state.sentLead.storedValues);
    const emailFromStorage = sentLeadFormValues?.email;
    const userEmail = emailFromStorage || emailFromProfile;

    // form setup
    const defaultFormValuesFromProfile = {
        email: userEmail,
        optIn: optInFromProfile
    };

    const methods = useFormRHF<{email: string; optIn: boolean}>({
        defaultValues: defaultFormValuesFromProfile,
        resolver: yupResolver(schema)
    });
    const {handleSubmit} = methods;

    useEffect(() => {
        if (defaultFormValuesFromProfile.email && methods.getValues("email") !== defaultFormValuesFromProfile.email) {
            methods.setValue("email", defaultFormValuesFromProfile.email);
        }
    }, [defaultFormValuesFromProfile]);

    const handleClick = () => {
        if ((userEmail && optInFromProfile) || isAuthenticated || articleAttachmentSavedEmail) {
            userComAttachmentDownload({
                email: methods.getValues("email"),
                attachmentTitle: props.title
            });

            window.open(props.downloadURL, target);
            return;
        }
        setModalPage("form");
    };

    const onSubmit = () => {
        storeEmail(methods.getValues("email"));
        userComAttachmentDownload({
            email: methods.getValues("email"),
            attachmentTitle: props.title,
            emailOptIn: methods.getValues("optIn")
        });

        setModalPage("success");
    };

    const onSecondStepDownload = () => {
        setModalPage(null);
    };

    return (
        <>
            <a className={ebookImageStyle} target={target} onClick={handleClick}>
                <Image className={pdfImg} src={source} alt={title} height="719px" width="465px" ratio={{xs: "465/719"}} />
            </a>
            {Boolean(modalPage) && (
                <Modal isOpen={Boolean(modalPage)} onModalClose={() => setModalPage(null)} modalTheme="light">
                    {modalPage === "form" ? (
                        <>
                            <div className={modalHeadingStyle}>
                                <BrandGuideIcon size="9" wrapperSize="10" wrapperType="circle" wrapperColor={getThemeVariable("colors.primary")} />
                                <div className={css({p: 2})}>
                                    <Text variant="headline_3">Pobierz darmowy materiał</Text>
                                    <Text variant="body_copy_2">Poszerz swoją wiedzę o zakupie mieszkania</Text>
                                </div>
                                <Modal.CloseButton placement="right" />
                            </div>
                            <Modal.Content className={modalStyle}>
                                <FormProviderRHF {...methods}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <InputField name="email" placeholder="e-mail" className={css({pb: 2})} disabled={Boolean(emailFromProfile)} />
                                        <CheckboxControlled
                                            id="optInCheckbox"
                                            name="optIn"
                                            labelContent="Chcę otrzymywać mailem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać."
                                            checkedMarkColor={getThemeVariable("globals.checkbox.checkedColor")}
                                            className={css({mb: 8})}
                                        />
                                        <Text variant="info_txt_2" align="center" color={getThemeVariable("colors.gray.700")} className={termsStyle}>
                                            Akceptuję{" "}
                                            <a href={rpAppLink.termsOfService.base()} target="_blank">
                                                regulamin serwisu
                                            </a>
                                        </Text>
                                        <Button variant="filled_primary" className={css({mb: 2})} fullWidth>
                                            pobierz
                                        </Button>
                                        <RodoText
                                            privacyPolicyUrl={rpAppLink.rodoTerms.base()}
                                            vendorName="Property Group sp. z o.o."
                                            privacyDataProcessorName={null}
                                            align="center"
                                        />
                                    </form>
                                </FormProviderRHF>
                            </Modal.Content>
                        </>
                    ) : (
                        <>
                            <BrandCheckboxCheckedIcon
                                className={successIconStyle}
                                size="9"
                                wrapperSize="10"
                                wrapperType="circle"
                                wrapperColor={getThemeVariable("colors.primary")}
                            />
                            <Modal.Content className={modalStyle}>
                                <div className={successTextWrapper}>
                                    <Text variant="headline_3" align="center">
                                        Materiał jest gotowy do pobrania
                                    </Text>
                                </div>
                                <Button
                                    href={downloadURL}
                                    target="_blank"
                                    variant="filled_primary"
                                    className={css({mb: 2})}
                                    iconLeft={DownloadIcon}
                                    onClick={onSecondStepDownload}
                                >
                                    pobierz
                                </Button>
                            </Modal.Content>
                        </>
                    )}
                </Modal>
            )}
        </>
    );
};

const ebookImageStyle = css({
    display: "block",
    width: "100%",
    cursor: "pointer"
});

const pdfImg = css({
    maxWidth: "32rem",
    width: "100%",
    height: "100%",
    marginLeft: "auto",
    marginRight: "auto",

    md: {
        maxWidth: "46.5rem"
    }
});

const modalStyle = css({
    p: 2,
    md: {
        p: 3,
        width: "556px"
    }
});

const modalHeadingStyle = css({
    p: 3,
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
});

const termsStyle = css({
    mb: 2,

    "& a, & a:hover, & a:visited, & a:active": {
        color: "inherit",
        textDecoration: "underline"
    }
});

const successTextWrapper = css({
    p: 2,
    mb: 2
});

const successIconStyle = css({
    mt: 2,
    mb: 0,
    mr: "auto",
    ml: "auto"
});
